import { useEffect, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { IExpand } from "../../../../types/expand.type";
import { asyncDelete, asyncGetList } from "../../../../utils/httpRequests";
import { TableColumn } from "react-data-table-component";
import CheckboxUpdate from "../../fast-update/CheckboxUpdate";
import { LuPlusCircle, LuTrash } from "react-icons/lu";
import WrapperExpand from "../WrapperExpand";
import TableApp from "../../tableapp/TableApp";
import Tablepagination from "../../tableapp/TablePagination";
import { useSelector } from "react-redux";
import FormAdd from "./FormAdd";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { isArray } from "lodash";

const columns: TableColumn<any>[] = [
  {
    name: "Hiển thị",
    width: "100px",
    selector: (row) => row.status,
    cell: (row) => {
      return (
        <CheckboxUpdate
          idUpdate={row?._id}
          defaultChecked={row?.status}
          apiCode="socialcategories"
          fieldUpdate="status"
        />
      );
    },
  },
  {
    name: "STT",
    width: "80px",
    wrap: true,
    selector: (row) => row.no,
  },
  {
    name: "Tên",
    width: "200px",
    wrap: true,
    selector: (row) => row.name,
  },
  {
    name: "Slug",
    width: "200px",
    wrap: true,
    selector: (row) => row.slug,
  },
  {
    name: "Mô tả",
    width: "200px",
    wrap: true,
    selector: (row) => row?.exfields?.subscription,
  },
  {
    name: "Nhóm người dùng",
    width: "150px",
    wrap: true,
    selector: (row) => row?.exfields?.group?.group_name,
  },
  {
    name: "SL bài",
    width: "120px",
    right: true,
    selector: (row) => row.post_count,
  },
  {
    name: "Lượt thích",
    width: "120px",
    right: true,
    selector: (row) => row.like_count,
  },
  {
    name: "Lượt comment",
    width: "130px",
    right: true,
    selector: (row) => row.comment_count,
  },
];

function ExpandSocialCategory({ data, onOpenEdit, setLoad }: IExpand) {
  const { showAlert } = useAlertContext();
  const { token, profile } = useSelector((state: any) => state.auth);
  const [subs, setSubs] = useState<any[]>([]);
  const [toggleCleared, setToggleCleared] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [paginationOptions, setPaginationOptions] = useState<{
    page: number;
    totalRows: number;
    limit: number;
  }>({
    page: 1,
    totalRows: 0,
    limit: 20,
  });
  const [refresh, setRefresh] = useState<number>(0);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [defaultValue, setDefaultValue] = useState<any>();

  const allowEditAndDelete =
    profile?.admin_app || profile?.email === data?.user_created;

  const handleCloseForm = () => {
    setIsEdit(false);
    setDefaultValue(undefined);
    setOpenForm(false);
  };

  const handleRowClicked = (row: any) => {
    setDefaultValue(row);
    setOpenForm(true);
    setIsEdit(true);
  };

  const getSubs = async () => {
    setLoading(true);
    const condition: { [key: string]: any } = {
      page: paginationOptions.page,
      limit: paginationOptions.limit,
      sort: { no: 1 },
      q: { parentId: data?._id },
    };

    const resp = await asyncGetList({
      apiCode: "socialcategories",
      token,
      condition,
    });

    const respCount = await asyncGetList({
      apiCode: "socialcategories",
      token,
      condition: { ...condition, count: 1 },
    });

    if (
      resp &&
      resp?.status === 200 &&
      respCount &&
      respCount?.status === 200
    ) {
      setSubs(resp?.data || []);
      setPaginationOptions((prev) => ({
        ...prev,
        totalRows: respCount?.data?.rows_number || 0,
      }));
    }
    setLoading(false);
  };

  // row per page change
  const handleRowPerPageChange = (value: number) => {
    setPaginationOptions({ ...paginationOptions, page: 1, limit: value });
  };

  const handleDelete = async () => {
    const resp = await asyncDelete({
      apiCode: "socialcategories",
      id: data?._id,
      token,
    });
    if (resp?.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi xóa dữ liệu",
      });
    } else {
      showAlert({ type: "success", message: `Đã xóa thành công` });
      setLoad?.((prev) => prev + 1);
    }
  };

  const handleDeleteSelectedRows = async () => {
    for (let i = 0; i < selectedRows.length; i++) {
      const resp = await asyncDelete({
        apiCode: "socialcategories",
        id: selectedRows[i]?._id,
        token,
      });
      if (resp?.status !== 200) {
        showAlert({
          type: "error",
          message: resp?.data?.error || resp?.data?.message || "Có lỗi xảy ra",
        });
        break;
      } else {
        if (i === selectedRows?.length - 1) {
          showAlert({
            type: "success",
            message: `Đã xóa thành công ${selectedRows?.length || 0} dòng`,
          });
          setRefresh((prev) => prev + 1);
          setToggleCleared(!toggleCleared);
        }
      }
    }
  };

  useEffect(() => {
    setRefresh((prev) => prev + 1);
  }, [paginationOptions?.page, paginationOptions.limit]);

  useEffect(() => {
    if (data?._id) {
      getSubs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?._id, refresh]);

  return (
    <>
      {openForm && (
        <FormAdd
          open={openForm}
          onClose={handleCloseForm}
          apiCode="socialcategories"
          defaultValues={
            defaultValue || { parentId: data?._id, parentName: data?.name }
          }
          isEdit={isEdit}
          setLoad={setRefresh}
        />
      )}
      <WrapperExpand
        data={data}
        action={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ py: 1 }}
          >
            {allowEditAndDelete && (
              <Button
                onClick={onOpenEdit}
                variant="contained"
                size="small"
                sx={{
                  boxShadow: "none",
                  textTransform: "none",
                  color: "common.white",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Chỉnh sửa
              </Button>
            )}
            {allowEditAndDelete && (
              <Button
                onClick={() => {
                  showAlert({
                    title: "Xác nhận xóa",
                    type: "info",
                    message: "Bạn có chắc muốn xóa dòng này không ?",
                    onConfirm: handleDelete,
                  });
                }}
                variant="contained"
                size="small"
                color="error"
                sx={{
                  boxShadow: "none",
                  textTransform: "none",
                  color: "common.white",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Xóa
              </Button>
            )}
          </Stack>
        }
      >
        <Stack
          gap={2}
          sx={{
            backgroundColor: "secondary.600",
            px: 2,
            py: 2,
            borderRadius: 1,
          }}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="h5">Danh mục con</Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Button
                onClick={() => setOpenForm(true)}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  boxShadow: "none",
                  color: "common.white",
                  textTransform: "none",
                  "&:hover": { boxShadow: "none" },
                }}
                startIcon={<LuPlusCircle size={16} />}
              >
                Thêm
              </Button>
              {isArray(selectedRows) && selectedRows?.length > 0 && (
                <IconButton
                  onClick={() => {
                    showAlert({
                      type: "info",
                      message: `Bạn có chắc muốn xóa ${selectedRows?.length} dòng đã chọn không ?`,
                      onConfirm: handleDeleteSelectedRows,
                    });
                  }}
                  sx={{
                    backgroundColor: "error.main",
                    color: "common.white",
                    borderRadius: 1,
                    height: "30px",
                  }}
                >
                  <LuTrash size={16} />
                </IconButton>
              )}
            </Stack>
          </Stack>
          {/* Table */}
          <Box
            sx={{
              width: "100%",
              height: `auto`,
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              <TableApp
                toggleCleared={toggleCleared}
                rowPerPage={paginationOptions.limit}
                columns={columns}
                data={subs}
                selectableRows
                fixedHeader
                progressPending={loading}
                onRowClicked={handleRowClicked}
                onSelectedRowsChange={setSelectedRows}
                fixedHeaderScrollHeight={`300px`}
              />
            </Box>
          </Box>
          {/* Pagination */}
          <Box
            sx={{
              mt: "auto",
              borderTop: "1px dashed",
              borderColor: "divider",
            }}
          >
            {paginationOptions.totalRows > 0 && (
              <Tablepagination
                currentPage={paginationOptions.page}
                onChangePage={(value) =>
                  setPaginationOptions({ ...paginationOptions, page: value })
                }
                rowsPerPage={paginationOptions.limit}
                rowCount={paginationOptions.totalRows}
                onChangeRowsPerPage={handleRowPerPageChange}
                paginationRowsPerPageOptions={[20, 50, 100, 500]}
              />
            )}
          </Box>
          {/* Pagination end */}
        </Stack>
      </WrapperExpand>
    </>
  );
}

export default ExpandSocialCategory;
